/* Program Name: CustomInputs.js
 **  Description: several pre-styled MUI buttons that will be commonly used
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-04-07  STT  Initial program
 ** SD6391     2024-08-02  SGS  SD-6762 - Added width param to CounterInput
 ** SD6391     2024-09-06  SGS  CounterInput now accepts bkgColor property
 ** SD6391     2024-10-31  SGS  AddressSelect display AddrLine2
 ** SD9567     2025-03-06  SGS  SD-9702 - CLV: Promo - View edit and targeting
 **                             screens for closed promotions
 ** SD9567    2025-04-16  sgs  SD9681 - CLV: Inventory Maintenance
 ********************************************************************************/
import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  TextField,
  FormControl,
  Tooltip,
  IconButton,
  Dialog,
  Button,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
} from '@mui/material'
import { Publish } from '@mui/icons-material'
import { limitFilterTo100 } from './filterOptions'
import AttributeEyeCon from '../StoreProfiles/AttirbuteEyeCon'

/**FORM TEXT FIELD
 //for text entry on standard forms in fuji
 * @param value       = String > the value that will be displayed by the input
 * @param fieldName   = String > the name of the field that the input is linked to
 * @param label       = String > the label for the field
 * @param handleText  = ()=>   > the function that triggers onChange
 * @param inputErrors = Obj    > an object that holds input error values no corresponding value no error
 */
export const FormTextField = ({
  value,
  fieldName,
  onInput,
  label,
  handleText,
  inputErrors,
  disabled,
  placeholder,
  multiline,
  rows,
  fullWidth,
  password,
  type,
  width,
  onKeyDown,
  maxLength,
  size,
  inputRef,
}) => {
  return (
    <TextField
      inputRef={inputRef}
      size={size ? size : 'small'}
      onKeyDown={onKeyDown}
      autoComplete={password ? 'current-password' : ''}
      type={type}
      //fullWidth={fullWidth}
      sx={{ m: 1, width: width ? width : fullWidth ? '100%' : 250 }}
      variant="outlined"
      color="secondary"
      onInput={onInput}
      multiline={multiline}
      rows={rows ? rows : null}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      error={inputErrors ? (inputErrors[fieldName] ? true : false) : false}
      helperText={inputErrors ? inputErrors[fieldName] : false}
      label={label}
      onChange={handleText(fieldName)}
      inputProps={
        maxLength
          ? {
              maxLength: maxLength,
            }
          : {}
      }
    />
  )
}

export const DollarField = ({
  label,
  value,
  fieldName,
  handleChange,
  fullWidth,
  width,
  size,
  disabled,
}) => {
  return (
    <FormControl sx={{ m: 1, width: width ? width : fullWidth ? null : 250 }}>
      <InputLabel htmlFor="outlined-adornment-amount">{label}</InputLabel>
      <OutlinedInput
        id="outlined-adornment-amount"
        label={label}
        disabled={disabled}
        size={size ? size : 'small'}
        inputProps={{
          step: '0.01',
        }}
        type="number"
        value={value}
        onChange={handleChange(fieldName)}
        startAdornment={<InputAdornment position="start">$</InputAdornment>}
      />
    </FormControl>
  )
}

/**Counter Input
 //for number entry on counters
 * @param value       = String   > the value that will be displayed by the input
 * @param onChange    = Function > function to ahndle controled input
 */
export const CounterInput = ({
  value,
  onChange,
  height,
  width,
  disabled,
  bkgColor,
}) => {
  return (
    <TextField
      disabled={disabled}
      onChange={onChange}
      sx={{
        width: width ? width : 40,
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
          {
            WebkitAppearance: 'none',
            margin: 0,
          },
      }}
      value={value}
      inputProps={{
        min: 0,
        style: {
          textAlign: 'center',
          padding: 0,
          height: height ? height : 40,
          backgroundColor: bkgColor ? bkgColor : 'transparent',
        },
      }}
    />
  )
}

/**FORM SELECT
 //for dropdown selectors on standard forms in fuji
 * @param options       = [Obj]  > an arry of options for the dropdown menu
 * @param value         = String > the value that will be displayed by the input
 * @param label         = String > the label for the field
 * @param handleSelect  = ()=>   > the function that triggers onChange
 */
export const FormSelect = ({
  options,
  value,
  label,
  handleSelect,
  fieldName,
  inputErrors,
  disabled,
  width,
  index,
  size,
  autoWidth,
  margin,
}) => {
  return (
    <FormControl
      error={inputErrors ? (inputErrors[fieldName] ? true : false) : false}
      margin="dense"
      size={size ? size : 'small'}
      sx={{ m: 1, width: width ? width : '100%' }}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        size={size}
        autoWidth={autoWidth}
        disabled={disabled}
        value={value}
        label={label}
        onChange={(event, value) => {
          handleSelect(value.props.value, fieldName, index)
        }}
      >
        {options &&
          options.map((option) => {
            return (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.disabled ? (
                  <>
                    <AttributeEyeCon />
                    &nbsp; {option.label}
                  </>
                ) : (
                  option.label
                )}
              </MenuItem>
            )
          })}
      </Select>
      <FormHelperText>
        {inputErrors
          ? inputErrors[fieldName]
            ? inputErrors[fieldName]
            : ''
          : ''}
      </FormHelperText>
    </FormControl>
  )
}

/**Address SELECT
 //for dropdown selectors on standard forms in fuji
 * @param options       = [Obj]  > an arry of options for the dropdown menu
 * @param value         = String > the value that will be displayed by the input
 * @param label         = String > the label for the field
 * @param handleSelect  = ()=>   > the function that triggers onChange
 */
export const AddressSelect = ({
  options,
  value,
  label,
  handleSelect,
  input,
  error,
  disabled,
}) => {
  return (
    <FormControl error={error ? true : false} sx={{ m: 1, minWidth: 250 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        size="large"
        label={label}
        variant="outlined"
        disabled={disabled}
        value={value}
        sx={{ height: 'fit-content' }}
        onChange={(event, value) => {
          handleSelect(value.props.value, input)
        }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              key={option.id}
              value={option.id}
              sx={{ backgroundColor: option.addrCity ? '' : '#2e7d3222' }}
            >
              <div>
                <div>{option.addrName}</div>
                <div>{option.addrAttn}</div>
                <div>{option.addr1}</div>
                <div>{option.addr2}</div>
                {option.addrCity && (
                  <div>
                    {option.addrCity},{' '}
                    {option.addrSt ? option.addrSt : option.addrCtry},{' '}
                    {option.addrZip}
                  </div>
                )}
              </div>
            </MenuItem>
          )
        })}
      </Select>
      <FormHelperText>{error ? error : ''}</FormHelperText>
    </FormControl>
  )
}

/**FORM FILE INPUT
 //for dropdown selectors on standard forms in fuji
 * @param options       = [Obj]  > an arry of options for the dropdown menu
 * @param displayField  = String > the name of the field of the Objs in options that will be displayed in the dropdown list
 * @param defaultValue  = String > the placeholder text for an empty field
 * @param value         = String > the value that will be displayed by the input
 * @param fieldName     = String > the name of the field that the input is linked to
 * @param label         = String > the label for the field
 * @param handleSelect  = ()=>   > the function that triggers onChange
 * @param inputErrors   = Obj    > an object that holds input error values no corresponding value no error
 */
export const FormFileInput = ({ file, setFile, text, tooltip }) => {
  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e) => {
    e.preventDefault()

    let newFile = e.dataTransfer.files[0].name
    if (newFile) {
      setFile(newFile)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        backgroundColor: '#d5d5d5',
        borderRadius: 5,
        width: 250,
        height: 60,
      }}
      onDrop={(e) => handleDrop(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDragEnter={(e) => handleDragEnter(e)}
    >
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="promo-img-file"
        type="file"
      />
      <label htmlFor="promo-img-file">
        <Tooltip disableInteractive title={tooltip}>
          <IconButton color="secondary" component="span">
            <Publish />
          </IconButton>
        </Tooltip>
      </label>
      <div>
        {text}
        <div>{file}</div>
      </div>
    </div>
  )
}

/**FORM FILE INPUT
 //for dropdown selectors on standard forms in fuji
 * @param options       = [Obj]  > an arry of options for the dropdown menu
 * @param displayField  = String > the name of the field of the Objs in options that will be displayed in the dropdown list
 * @param defaultValue  = String > the placeholder text for an empty field
 * @param value         = String > the value that will be displayed by the input
 * @param fieldName     = String > the name of the field that the input is linked to
 * @param label         = String > the label for the field
 * @param handleSelect  = ()=>   > the function that triggers onChange
 * @param inputErrors   = Obj    > an object that holds input error values no corresponding value no error
 */
export const FormImageFileInput = ({ file, setFile, text, tooltip }) => {
  const [modal, setModal] = useState(false)
  const image = useRef(null)

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e) => {
    e.preventDefault()

    const [newFile] = e.dataTransfer.files
    if (newFile) {
      const reader = new FileReader()
      const { current } = image
      current.newFile = newFile
      reader.onload = (e) => {
        current.src = e.target.result
      }
      reader.readAsDataURL(newFile)
      setFile(newFile.name)
    }
  }

  const handleFile = (e) => {
    const [newFile] = e.target.files
    if (newFile) {
      const reader = new FileReader()
      const { current } = image
      current.newFile = newFile
      reader.onload = (e) => {
        current.src = e.target.result
      }
      reader.readAsDataURL(newFile)
      setFile(newFile.name)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        backgroundColor: '#d5d5d5',
        borderRadius: 5,
        width: 250,
        height: 60,
      }}
      onChange={handleFile}
      onDrop={(e) => handleDrop(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDragEnter={(e) => handleDragEnter(e)}
    >
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="promo-img-file"
        type="file"
      />
      <label htmlFor="promo-img-file">
        <Tooltip disableInteractive title={tooltip}>
          <IconButton color="secondary" component="span">
            <Publish />
          </IconButton>
        </Tooltip>
      </label>
      <div>
        {text}
        <div>
          <Button
            onClick={() => {
              setModal(true)
            }}
          >
            {file}
          </Button>
        </div>
      </div>
      <img alt={'Uploaded'} ref={image} style={{ display: 'none' }} />
      <Dialog
        open={modal}
        onClose={() => {
          setModal(false)
        }}
      >
        {image.current && (
          <img
            alt={'Uploaded'}
            src={image.current.currentSrc}
            style={{
              maxWidth: 1200,
              maxHeight: 750,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        )}
      </Dialog>
    </div>
  )
}

export const AsyncSelect = ({
  limitFilter100,
  label,
  getAPI,
  getAPIValue,
  handleSelect,
  overrideSx,
  defaultValue,
  defaultInputValue,
  lazyLoad,
  size,
}) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [error, setError] = useState(false)
  const isEmpty = options.length === 0
  const loading = open && isEmpty
  const [controlledValue, setControlledValue] = useState(defaultValue || '')
  const [inputValue, setInputValue] = useState(defaultInputValue || '')

  const emptyOptions = () => {
    setOptions([])
  }

  const handleError = () => {
    emptyOptions()
    setOpen(false)
    setError(true)
  }

  useEffect(() => {
    setControlledValue(defaultValue || '')
    setInputValue(defaultInputValue || '')
  }, [defaultInputValue])

  useEffect(() => {
    let active = true

    if (!lazyLoad) {
      setOptions([])
    }

    if (open && isEmpty) {
      getAPI()
        .then((response) => {
          if (!response.data.meta[0].err) {
            if (active) {
              if (response.data[getAPIValue]) {
                setOptions(response.data[getAPIValue])
              } else {
                handleError()
              }
            }
          } else {
            handleError()
          }
        })
        .catch((error) => {
          //alert.current.errorMessage(error.message);
        })
        .then(() => {
          //get();
        })
    }

    return () => {
      active = false
    }
  }, [open, getAPIValue])

  return (
    <Autocomplete
      size={size ? size : 'small'}
      value={controlledValue}
      onChange={(event, newValue) => {
        setControlledValue(newValue ? newValue.value : null)
        setInputValue(newValue ? newValue.label : '')
        handleSelect(newValue ? newValue.value : null)
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason !== 'reset') {
          setInputValue(newInputValue)
        }
      }}
      isOptionEqualToValue={(option, value) =>
        parseInt(option.value) === parseInt(value)
      }
      id="async-select"
      sx={{ width: '100%', m: 1, minWidth: 250, ...overrideSx }}
      open={open}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      filterOptions={limitFilter100 ? limitFilterTo100 : null}
      getOptionLabel={(option) =>
        option.label ? option.label : option.title ? option.title : ''
      }
      options={options}
      loading={loading}
      renderInput={(params) => (
        <TextField
          size="small"
          {...params}
          error={error}
          variant="outlined"
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
